/**
 * Closing sequence — terminal moment after the catalog ends.
 *
 * Mirrors the opening boot sequence in visual language. After the
 * timeline finishes the coda, this fades in over the catalog and
 * frames the whole parchment experience as a render that just
 * exited: the agent's stage handed back to gastown, the skill
 * marked complete.
 *
 * Structure (matches the boot's rhythm beat-for-beat):
 *
 *   1. [stage] summary stats     — entries, words, ascii cells, effects
 *   2. [mayor] polecat lineage   — model attribution per agent
 *   3. [gastown] pitch block     — five-line "what this is" close
 *   4. signoff + [hermes] stamp  — submission line + final prompt
 *
 * Real numbers come from the deployed manifest and the run logs in
 * pipeline/runs/. See timeline-of-changes commit for source-of-truth.
 *
 * Roughly 12-14 seconds total, then leaves the user on a final empty
 * prompt with a blinking cursor.
 */

// Each line: { text, delayBefore, typeSpeed, color?: string }
// All times in ms. typeSpeed is ms per character; 0 = render instant.
//
// Color palette in this sequence (mirrors the boot's three-color
// triptych — amber → pink → green — with green carrying the
// orchestration weight here):
//   --boot-system  : system-orchestration agents
//                    ([stage], [mayor], [gastown], [hermes])
//   --boot-kimi    : ALL polecat lines, regardless of which model
//                    they ran on ([writer], [illustrator],
//                    [animator], [integrator]) — pink is the
//                    "polecat tier" color in this build
//   --boot-ok      : success status — sage green ✓ marks. ✓ is
//                    pulled into its own span by typeLine so it
//                    doesn't inherit the polecat line's pink.
//   kimi @ 60%     : the writer's in/out char count sub-stat line —
//                    same kimi pink as the [writer] tier above but
//                    at 60% alpha (via color-mix) so it reads as a
//                    secondary detail without breaking color tier
//   --boot-accent  : final prompt cursor — same amber as the
//                    boot's "$ gastown run bestiary" prompt to
//                    bookend with the experience's first line.
const CLOSING_LINES = [
  // ── [runtime] stack manifest header ──────────────────────────
  // First lines of the closing log — names the orchestration stack
  // by version. Sits in the green system tier alongside [stage],
  // [mayor], [gastown], [hermes]. Split into two lines because the
  // single-line form (orchestration + delivery on one row) runs to
  // 84 chars, over the closing's /48 font-size cap (76-char ceiling).
  // Top row = orchestration agents, bottom row = delivery layer.
  // Version on hermes-skill pulled from package.json (the project IS
  // the skill; they share a version stamp). Claude is named without a
  // version because the host Claude Code binary in ~/.hermes/config.yaml
  // resolves the model at invocation time rather than pinning one.
  { text: "[runtime]    hermes-skill@0.1 · claude · kimi-k2-0905 · flux-1.1-pro", delayBefore: 200, typeSpeed: 5, color: "var(--boot-system)" },
  { text: "[runtime]    vite + vercel", delayBefore: 80, typeSpeed: 4, color: "var(--boot-system)" },
  { text: "", delayBefore: 350 },

  // ── [stage] summary block ────────────────────────────────────
  { text: "[stage]      catalog rendered", delayBefore: 250, typeSpeed: 8, color: "var(--boot-system)" },
  { text: "[stage]      10 entries · 1 preface · 1 coda", delayBefore: 200, typeSpeed: 5, color: "var(--boot-system)" },
  { text: "[stage]      1,504 words · 1,081,800 ascii cells · 600 × 180 grid", delayBefore: 180, typeSpeed: 4, color: "var(--boot-system)" },
  { text: "[stage]      62 sustained effects · 1 focal eye-blink", delayBefore: 180, typeSpeed: 4, color: "var(--boot-system)" },
  { text: "", delayBefore: 350 },

  // ── [mayor] polecat lineage block ────────────────────────────
  // Mirrors the boot's "[mayor] dispatching polecats" beat —
  // each agent gets one summary line with model attribution,
  // matching the boot's ☾ kimi prefix and per-line color rules.
  { text: "[mayor]      polecats:  writer · illustrator · animator · integrator", delayBefore: 100, typeSpeed: 5, color: "var(--boot-system)" },
  { text: "[writer]     ☾ kimi-k2-0905    voice pass on 10 entries          ✓", delayBefore: 250, typeSpeed: 5, color: "var(--boot-kimi)" },
  { text: "[writer]     20,989 in → 10,131 out · 135 s", delayBefore: 80, typeSpeed: 4, color: "color-mix(in srgb, var(--boot-kimi) 60%, transparent)" },
  { text: "[illustrator] flux-1.1-pro     10 paintings · 41 MB png sources  ✓", delayBefore: 220, typeSpeed: 4, color: "var(--boot-kimi)" },
  { text: "[animator]   ☾ kimi-k2-0905    10 choreography scripts            ✓", delayBefore: 220, typeSpeed: 5, color: "var(--boot-kimi)" },
  { text: "[integrator] manifest.json      12 scenes wired to the runtime", delayBefore: 200, typeSpeed: 4, color: "var(--boot-kimi)" },
  { text: "", delayBefore: 500 },

  // ── [gastown] "what this is" pitch ───────────────────────────
  // Slower typeSpeed (~9ms/char) so the prose reads at a
  // human-readable pace rather than streaming as fast as the
  // log lines above. Five lines, ~60-68 chars each.
  { text: "[gastown]    bestiary of the lost — ten paintings of bosch", delayBefore: 100, typeSpeed: 9, color: "var(--boot-system)" },
  { text: "[gastown]    no longer extant. descriptions invented and wrapped", delayBefore: 80, typeSpeed: 9, color: "var(--boot-system)" },
  { text: "[gastown]    around a real archival void. the rendering ghosts on", delayBefore: 80, typeSpeed: 9, color: "var(--boot-system)" },
  { text: "[gastown]    purpose: the painting never fully resolves, the catalog", delayBefore: 80, typeSpeed: 9, color: "var(--boot-system)" },
  { text: "[gastown]    never claims final authority.", delayBefore: 80, typeSpeed: 9, color: "var(--boot-system)" },
  { text: "", delayBefore: 600 },

  // ── Signoff + [hermes] submission stamp ──────────────────────
  { text: "[mayor]      handing back to gastown", delayBefore: 100, typeSpeed: 6, color: "var(--boot-system)" },
  { text: "[gastown]    skill exited (0)", delayBefore: 250, typeSpeed: 6, color: "var(--boot-system)" },
  { text: "[hermes]     submitted · agent creative hackathon · nous research · may 2026", delayBefore: 350, typeSpeed: 6, color: "var(--boot-system)" },
  { text: "", delayBefore: 400 },

  // ── Final prompt with blinking cursor ────────────────────────
  // Only "$ " is typed. The terminal cursor (underscore) is appended
  // after the loop completes via runClosingSequence — so the blink
  // begins exactly when the rest of the text has finished typing,
  // matching the cadence of a real terminal awaiting input.
  { text: "$ ", delayBefore: 200, typeSpeed: 22, color: "var(--boot-accent)" },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Keep the latest typed character pinned to the bottom of the
 * scrollable closing-sequence container so the typing tip stays in
 * view as the ~22-line log streams in on shorter viewports.
 */
function scrollToBottom(target) {
  const scroller = target.parentElement || target;
  scroller.scrollTop = scroller.scrollHeight;
}

async function typeLine(line, target) {
  if (line.delayBefore) await sleep(line.delayBefore);
  if (!line.text) {
    target.appendChild(document.createElement("br"));
    scrollToBottom(target);
    return;
  }
  // Primary span carries the line's tier color (system green / kimi
  // pink / dim warm / amber prompt). The ✓ checkmark gets pulled
  // into its own sage span so it reads as a success-status mark
  // independent of the polecat's tier color.
  let span = document.createElement("span");
  if (line.color) span.style.color = line.color;
  target.appendChild(span);
  for (const ch of line.text) {
    if (ch === "✓") {
      const checkSpan = document.createElement("span");
      checkSpan.style.color = "var(--boot-ok)";
      checkSpan.textContent = "✓";
      target.appendChild(checkSpan);
      // Open a fresh primary-color span in case more characters
      // follow the ✓ on this line.
      span = document.createElement("span");
      if (line.color) span.style.color = line.color;
      target.appendChild(span);
    } else {
      span.textContent += ch;
    }
    scrollToBottom(target);
    if (line.typeSpeed) await sleep(line.typeSpeed);
  }
  target.appendChild(document.createElement("br"));
  scrollToBottom(target);
}

/**
 * Run the closing sequence. Resolves after the sequence has played
 * and the screen has settled. Does NOT fade out — leaves the user
 * on the final terminal prompt with a blinking cursor (the experience
 * has ended; nothing more to advance to).
 */
export async function runClosingSequence() {
  const root = document.getElementById("closing-sequence");
  const out = document.getElementById("closing-output");
  if (!root || !out) return;
  out.textContent = "";

  // Show, fade in
  root.classList.add("active");
  await sleep(50);
  root.classList.add("visible");

  // Stream the lines
  for (const line of CLOSING_LINES) {
    await typeLine(line, out);
  }

  // Typing finished. Drop the running cursor (the ▌ that follows
  // typing via #closing-output::after) and append a stable blinking
  // underscore at the prompt — the "awaiting input" terminal cue.
  // The .done class hides the ::after cursor; .closing-cursor blinks
  // via CSS at a 1s step-end cycle.
  //
  // typeLine appends a trailing <br> after every line (including the
  // final "$ " prompt). Without removing it, the cursor span lands on
  // its own next line — visually `$` and `_` stack vertically. Strip
  // the trailing <br> so the cursor sits inline next to the prompt.
  const lastChild = out.lastElementChild;
  if (lastChild && lastChild.tagName === "BR") {
    out.removeChild(lastChild);
  }
  out.classList.add("done");
  out.insertAdjacentHTML(
    "beforeend",
    '<span class="closing-cursor" aria-hidden="true">_</span>',
  );
  scrollToBottom(out);

  // Hold on the final prompt — the experience is over.
}
